import useProductQueries from '@/dashboard/hooks/queries/useProductQueries';
import { pageKey } from '@/dashboard/lib/queryKeys';
import usePage from '@dashboard/hooks/usePage';
import { useQueryClient, useMutation } from '@tanstack/react-query';

/**
 * Hook for managing product data and mutations
 * @returns {Object} Product data and update functions
 */
export default function useProduct() {
  const { page } = usePage();
  const { updateProduct: updateProductMutation } = useProductQueries();
  const queryClient = useQueryClient();

  const updateProduct = useMutation({
    mutationFn: values => {
      if (values.isNew) {
        return updateProductMutation(values);
      }

      const updateTimestamp = Date.now();
      return updateProductMutation(
        {
          ...values,
          _lastUpdateTimestamp: updateTimestamp,
        },
        page.product._id,
      );
    },
    onMutate: async values => {
      if (values.isNew) {
        return values;
      }

      await queryClient.cancelQueries({ queryKey: pageKey(page._id) });

      const previousData = queryClient.getQueryData(pageKey(page._id));

      const updateTimestamp = values._lastUpdateTimestamp || Date.now();

      queryClient.setQueryData(pageKey(page._id), old => {
        if (old?._lastUpdateTimestamp && old._lastUpdateTimestamp > updateTimestamp) {
          return old;
        }

        return {
          ...old,
          product: { ...old.product, ...values },
          _lastUpdateTimestamp: updateTimestamp,
        };
      });

      return {
        previousData,
        updateTimestamp,
      };
    },
    onSuccess: () => {
      if (page._id) {
        queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
      }
    },
    onError: (error, values, rollback) => {
      if (rollback) rollback();
    },
    retry: 1,
  });

  return {
    product: page.product,
    updateProduct,
  };
}
